import { useAuthInfo } from '@propelauth/react';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';

import { APIBaseChronos } from '../hosts';
import { QueryEntity } from '../types';
import useGetFetchConfig, { FetchMethod } from '../useGetFetchConfig';
import { editFactInVisualisation } from '@/new/pages/matters/:matterId/subsets/:subsetId/chronologies/components/visual-timeline/syncing-helpers/syncVisualisationWithTimelineChanges';
import { ChronologyFact } from '@/types';

interface UseUpdateChronologyFactWithSync {
  field: keyof ChronologyFact;
  value: ChronologyFact[keyof ChronologyFact];
  factId: string;
  chronologyId: string;
}

const useUpdateChronologyFactWithSync = ({ matterId, subsetId }: { matterId: string; subsetId: string }) => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();
  const authInfo = useAuthInfo();

  return useMutation(
    async ({ field, value, factId, chronologyId }: UseUpdateChronologyFactWithSync) => {
      const fetchConfigPatch = getFetchConfig({ method: FetchMethod.PATCH, data: { [field]: value } });

      const response = await fetch(
        `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/chronology/${chronologyId}/fact/${factId}`,
        fetchConfigPatch,
      );

      if (!response.ok) {
        toast.error('Failed to update field');
        throw new Error('Failed to update field');
      }

      const updatedFactFromDB = await response.json();

      return { updatedFact: updatedFactFromDB, chronologyId };
    },
    {
      onSuccess: async ({ updatedFact, chronologyId }) => {
        // Invalidate chronology query first
        queryClient.invalidateQueries({ queryKey: [QueryEntity.Chronology, { chronologyId }] });
        queryClient.invalidateQueries({ queryKey: [QueryEntity.ChronologyFacts, { chronologyId }] });

        toast.success('Chronology fact updated');
        // Sync with visual timeline
        try {
          await editFactInVisualisation({
            matterId,
            subsetId,
            chronologyId,
            updatedFact,
            getAccessToken: authInfo.tokens.getAccessToken,
          });

          queryClient.invalidateQueries([QueryEntity.ChronologyVisualisation, { subsetId, chronologyId }]);
        } catch (error) {
          console.error('Failed to sync updated fact with visual timeline:', error);
        }
      },
      onError: (error) => {
        console.error('Update Error: ', error);
        toast.error('Failed to update field');
      },
    },
  );
};

export default useUpdateChronologyFactWithSync;
